import { z } from 'zod';

/**
 * Per-user `answer-question` cache row (#331). Lives in `@outlinked/shared`
 * so the cloud `getQaCacheEntry` read path can validate the row coming
 * back from DDB even though `@outlinked/db` doesn't itself declare a
 * zod dependency — convention for the project: schemas live in shared,
 * entity files do thin parse-on-read defense.
 *
 * Schema-on-read defense. Writes go through `putQaCacheEntry` which only
 * accepts a typed argument, but the row in DDB can drift via: (a) a
 * future migration that drops or renames a column; (b) a manual
 * operator edit; (c) a partial-row resurrection that escaped the
 * `touchQaCacheHit` ConditionExpression guard. Any of those would have
 * `r.Item as QaCacheEntry` silently return a malformed object — the
 * route would then ship `{ answer: undefined, confidence: undefined,
 * ... }` to the worker. Parsing on read flips the malformed row to a
 * cache-miss; next answer-question call repopulates cleanly.
 */
export const QaCacheEntrySchema = z.object({
  userId: z.string().min(1),
  /** SHA-256 hex of normalized question text. 64 chars. */
  hash: z.string().regex(/^[a-f0-9]{64}$/),
  /** The actual question text (kept for debugging + admin tooling). */
  questionText: z.string().min(1),
  /** The cached answer string. */
  answer: z.string(),
  /** Model that produced the answer (dispatched, not requested — #323). */
  model: z.string().min(1),
  /** LLM-reported (or heuristic) confidence in [0, 1]. */
  confidence: z.number().min(0).max(1),
  /** ISO-8601 of last cache hit. Refreshed on every read. */
  lastUsedAt: z.string().min(1),
  /** Count of cache hits since this row was written. */
  hitCount: z.number().int().nonnegative(),
  /** Epoch-seconds DDB native TTL. */
  ttl: z.number().int().positive(),
});
export type QaCacheEntry = z.infer<typeof QaCacheEntrySchema>;
